<template>
    <div>
        <div
            v-if="isLightbox"
            class="h-[35px]"
        />

        <div
            class="bg-gray-100 py-2 text-center text-gray-600"
            :class="[
                isLightbox ? 'fixed bottom-0 left-0 right-0 z-20' : '',
                isKiosk ? 'relative' : ''
            ]"
        >
            <button
                v-if="isKiosk"
                type="button"
                class="absolute left-0 top-1/2 flex -translate-y-1/2 transform items-center gap-2 px-4 py-2"
                @click="$emit('verify')"
            >
                <check-circle-icon
                    v-if="kioskName === undefined || kioskName === ''"
                    class="h-6 w-6"
                />
                <shield-check-icon
                    v-else
                    class="h-6 w-6"
                />
                {{ kioskName }}
            </button>

            <div
                class="flex items-center justify-center gap-3 px-4"
                :class="isKiosk ? 'pl-24 pr-4' : ''"
            >
                <label
                    v-if="showLocaleSwitcher"
                    class="relative inline-flex items-center"
                >
                    <span class="sr-only">{{
                        t('layout.localeSwitcher')
                    }}</span>
                    <select
                        v-model="selectedLocale"
                        class="appearance-none rounded border border-gray-300 bg-white px-2 py-1 pr-7 text-xs font-semibold text-gray-600 shadow-sm outline-none transition-colors hover:border-gray-400 focus:border-gray-500"
                    >
                        <option
                            v-for="localeOption in localeOptions"
                            :key="localeOption.value"
                            :value="localeOption.value"
                        >
                            {{ localeOption.label }}
                        </option>
                    </select>
                    <span
                        class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400"
                    >
                        ▼
                    </span>
                </label>

                <div>
                    <i18n-t
                        keypath="layout.poweredBy"
                        scope="global"
                        tag="span"
                    >
                        <template #company>
                            <nuxt-link
                                to="https://www.redpointhq.com"
                                target="_blank"
                                class="font-semibold hover:underline"
                                >Redpoint HQ</nuxt-link
                            >
                        </template>
                        <template #version>
                            <span class="text-xs text-gray-300">
                                &mdash;&nbsp;{{ version.major }}.{{
                                    version.minor
                                }}.{{ version.patch }}
                            </span>
                        </template>
                    </i18n-t>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { version } from '~/composables/meta'
import NuxtLink from '#app/components/nuxt-link'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { ShieldCheckIcon } from '@heroicons/vue/24/solid'
import { CheckCircleIcon } from '@heroicons/vue/24/outline'
import { useI18n, I18nT } from 'vue-i18n'
import { computed } from 'vue'
import { useLocalization } from '~/composables/use-localization'
import type { SupportedLocale } from '~/composables/create-language-context'

defineProps<{
    kioskName?: string
}>()

defineEmits<{
    (e: 'verify'): void
}>()

const { isLightbox, isKiosk } = useLayoutMode()
const { locale, setLocale, t } = useI18n({ useScope: 'global' })
const { availableLocales } = useLocalization()

const localeOptions = computed(() => {
    return availableLocales.value
})

const showLocaleSwitcher = computed(() => {
    // Only show the switcher when the storefront exposes more than one enabled language.
    return localeOptions.value.length > 1
})

const selectedLocale = computed<SupportedLocale>({
    get() {
        // Keep the native select synchronized with the active locale string.
        return locale.value as SupportedLocale
    },
    set(nextLocale) {
        void setPreferredLocale(nextLocale)
    }
})

// Route all locale changes through Nuxt i18n so the locale cookie is updated for SSR and refreshes.
async function setPreferredLocale(nextLocale: SupportedLocale) {
    await setLocale(nextLocale)
}
</script>
